import { buildAroValidationContext } from './aroValidationContext'
import { buildMaxCharRule } from './validationRuleBuilders'

/**
 * Builds the Benefit Distribution validation context by extending the shared ARO base
 * context with BD-specific field rules.
 *
 * Shared fields (recordkeeper, tpa, planSponsor, planName, planEin, planNumber,
 * firstName, middleName, lastName, addressLine1, addressLine2, city, ssn,
 * emailAddress, audience flags, and pure-validator formats: ssnFormat, dobFormat,
 * zipCodeFormat) are inherited from buildAroValidationContext unchanged.
 * BD-specific fields are layered on top without altering shared behavior.
 *
 * All RegExp objects are compiled once at registration time and reused across every
 * record evaluation, avoiding per-record 
ew RegExp(...) construction.
 *
 * @param {string} userAudience - e.g. 'Recordkeeper', 'TPA'
 * @returns {object} Validation context for BD listeners and pure validators
 */
export function buildBdValidationContext(userAudience) {
  const base = buildAroValidationContext(userAudience)

  return {
    // ── All shared ARO fields (recordkeeper, tpa, planSponsor, planName, planEin,
    //    planNumber, firstName, middleName, lastName, addressLine1, addressLine2,
    //    city, ssn, emailAddress, audience flags, and pure-validator formats:
    //    ssnFormat, dobFormat, zipCodeFormat) ────────────────────────────────────
    ...base,

    // ── BD-specific field rules ────────────────────────────────────────────────
    phone: {
      phoneNumber: {
        re: /^(\+|\(|\d)[0-9a-zA-Z ,.()-]{2,35}$/,
        error:
          'Must start with +, (, or a number and must follow a standard formatting convention. E.g., +01 (555) 243-0321 ext. 44',
      },
    },
    transactionType: {
      validValues: {
        re: /^(?:check|wire|ACH|check\/overnight delivery)$/i,
        error: 'Must be check, wire, ACH, or check/overnight delivery',
      },
    },
    paymentInstructions: {
      validValues: {
        re: /^(?:direct to participant|direct rollover)$/i,
        error: "Must be 'direct to participant' or 'direct rollover'",
      },
    },
    amount: {
      maxChar: buildMaxCharRule(14),
    },
    tpaBenefitDistributionProcessingFee: {
      maxChar: buildMaxCharRule(14),
    },
    inspiraBenefitDisbursementProcessingFee: {
      maxChar: buildMaxCharRule(14),
    },
    grossDistributionAmount: {
      maxChar: buildMaxCharRule(14),
    },
    wireAch: {
      maxChar: buildMaxCharRule(22),
    },
    abaRouting: {
      format: {
        re: /^\d{9}$/,
        error:
          'Nine-digit number only, social security number (SSN) unaccepted',
      },
    },
    wireAch3: {
      format: {
        re: /^\d{9}$/,
        error: 'Nine-digit number only',
      },
    },
    ffcName: {
      format: {
        re: /[A-Za-z]/,
        error: 'String must have at least one alpha character',
      },
    },
    ffcNumber: {
      format: {
        re: /^(?=.*\d).+$/,
        error: 'Must have at least one numeric character.',
      },
    },
    accountType: {
      validValues: {
        re: /^(?:checking|savings)$/i,
        error: "Must be 'checking' or 'savings'",
      },
    },
    checkPayableInfo: {
      maxChar: buildMaxCharRule(36),
    },
    accountNumber: {
      format: {
        re: /^(?=.*\d).+$/,
        error: 'Must have at least one numeric character',
      },
    },
    addressForCheck: {
      maxChar: buildMaxCharRule(36),
    },
    cityForCheck: {
      maxChar: buildMaxCharRule(22),
    },
    stateForCheck: {
      maxChar: buildMaxCharRule(22),
    },
    zipForCheck: {
      maxChar: buildMaxCharRule(10),
    },
    grossDistribution: {
      maxChar: buildMaxCharRule(14),
    },
    taxableAmount: {
      maxChar: buildMaxCharRule(14),
    },
    taxableAmountNotDetermined: {
      validValues: {
        re: /^X?$/i,
        error: "Value must be 'X' or blank",
      },
    },
    totalDistribution: {
      validValues: {
        re: /^X?$/i,
        error: "Value must be 'X' or blank",
      },
    },
    capitalGain: {
      maxChar: buildMaxCharRule(14),
    },
    federalIncomeTax: {
      maxChar: buildMaxCharRule(7),
    },
    federalIncomeTaxAmount: {
      maxChar: buildMaxCharRule(14),
    },
    employeeContributions: {
      maxChar: buildMaxCharRule(14),
    },
    netAppreciation: {
      maxChar: buildMaxCharRule(14),
    },
    distributionCode: {
      invalidFormat: {
        re: /^([A-Za-z]\d?|\d)$/,
        error:
          'Invalid format, must be either a single number, a single letter or a letter and number combination',
      },
      lengthCheck: {
        re: /^.{1,2}$/,
        error: 'Must be 1 to 2 characters',
      },
    },
    percentageTotalDistribution: {
      maxChar: buildMaxCharRule(7),
    },
    totalEmployeeContributions: {
      maxChar: buildMaxCharRule(14),
    },
    amountAllocable: {
      maxChar: buildMaxCharRule(14),
    },
    firstYearContrib: {
      format: {
        re: /^(19\d{2}|20\d{2})$/,
        error: 'Format must be: CCYY',
      },
    },
    stateTaxWithheld: {
      maxChar: buildMaxCharRule(7),
    },
    stateTaxWithheldAmount: {
      maxChar: buildMaxCharRule(14),
    },
    payerStateNumber: {
      maxChar: buildMaxCharRule(10),
    },
  }
}
